import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef } from 'react'

import type { ChatMessage } from '@/entities/ai-chat'

import { ArrowUpSvg } from '@/shared/lib/icons/system/icons/arrow-up-svg'
import { Button } from '@/shared/ui'

import { MessageItem } from '../message-item/MessegeItem'

import cl from './message-list.module.scss'

export type MessageListHandle = {
	scrollToBottom: () => void
}

type MessageListProps = {
	messages: ChatMessage[]
	streamMessage: string
	streamStatus: string
	onLoad: () => void
	showMoreButton: boolean
	isMoreLoading: boolean
	hideStatus?: boolean
}

const splitStreamStatus = (status: string): { icon: string | null; text: string } => {
	const match = status.match(/^(\p{Extended_Pictographic}\uFE0F?)\s*(.*)$/u)

	if (!match) {
		return { icon: null, text: status }
	}

	return { icon: match[1], text: match[2] }
}

export const MessageList = forwardRef<MessageListHandle, MessageListProps>(
	({ messages, streamMessage, streamStatus, onLoad, showMoreButton, isMoreLoading, hideStatus }, ref) => {
		const messagesEndRef = useRef<HTMLDivElement>(null)
		const containerRef = useRef<HTMLDivElement>(null)
		const scrollHeightBeforeRef = useRef(0)

		useLayoutEffect(() => {
			if (!containerRef.current) return

			const scrollHeightAfter = containerRef.current.scrollHeight
			const heightDifference = scrollHeightAfter - scrollHeightBeforeRef.current

			containerRef.current.scrollTop += heightDifference
		}, [messages, streamMessage, streamStatus])

		useEffect(() => {
			scrollToBottom()
		}, [])

		const loadPreviousMessages = async () => {
			if (containerRef.current) {
				scrollHeightBeforeRef.current = containerRef.current.scrollHeight
			}

			onLoad?.()
		}

		const scrollToBottom = () => {
			messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
		}

		useImperativeHandle(ref, () => ({
			scrollToBottom
		}))

		const { icon: streamStatusIcon, text: streamStatusText } = splitStreamStatus(streamStatus)

		return (
			<div className={cl.messagesList} ref={containerRef}>
				{showMoreButton && (
					<Button
						className={cl.messagesList__show_more}
						onClick={loadPreviousMessages}
						disabled={isMoreLoading}
						theme="outlined">
						{isMoreLoading ? 'Загрузка...' : 'Показать еще'}
						<ArrowUpSvg />
					</Button>
				)}
				{messages.map(message => (
					<MessageItem key={message.id} message={message} />
				))}
				{streamMessage && (
					<MessageItem
						message={{
							id: 'stream-message',
							content: streamMessage,
							role: 'assistant'
						}}
					/>
				)}
				{streamStatus && !hideStatus && (
					<div className={cl.streamStatus}>
						{streamStatusIcon && <span className={cl.streamStatus__icon}>{streamStatusIcon}</span>}
						{streamStatusText && <span className={cl.streamStatus__text}>{streamStatusText}</span>}
					</div>
				)}
				<div ref={messagesEndRef} />
			</div>
		)
	}
)
